import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./index.css";
import MenuBar from "./Mac/MenuBar";
import DesktopIcon from "./Mac/DesktopIcon";
import MemoWindow from "./Mac/MemoWindow";
import ConfirmDialog from "./Mac/ConfirmDialog";
import TrashIcon from "./Mac/TrashIcon";
import TrashWindow from "./Mac/TrashWindow";

const STORAGE_KEY = "codevex:memos:v1";
const SNAP_KEY = "codevex:snapToGrid:v1";
const ABOUT_ID = "system:about-me";
const SNAKE_ID = "system:snake";
const ABOUT_NAME = "About Me";
const DESKTOP_GRID = Object.freeze({
  margin: 14,
  cellW: 104,
  cellH: 100,
  trashSafePadX: 140,
  trashSafePadY: 170,
});
const ABOUT_CONTENT = `CodeVex is a software development company specializing in Web3—especially decentralized finance (DeFi) on the Ethereum Virtual Machine (EVM).

We cover the full stack: backend services, UI/UX, smart contracts, and polished frontend applications.

CodeVex has helped build protocols that have garnered tens of millions in TVL without a single security breach, including Vaporwave Finance and Circuit Protocol.

With an eye for aesthetics and a bias for shipping, we bring serious engineering discipline to fun, memorable products.

Reach out: contact@codevex.xyz`;

function getDesktopLayout(desktopEl) {
  const rect = desktopEl?.getBoundingClientRect?.();
  const width = rect?.width ?? window.innerWidth;
  const height = rect?.height ?? Math.max(0, window.innerHeight - 28);

  const { margin, cellW, trashSafePadX } = DESKTOP_GRID;
  const usableW = Math.max(cellW, width - trashSafePadX);
  const cols = Math.max(1, Math.floor((usableW - margin * 2) / cellW));
  return { width, height, cols };
}

function slotToPos(slot, cols) {
  const { margin, cellW, cellH } = DESKTOP_GRID;
  const col = slot % cols;
  const row = Math.floor(slot / cols);
  return { ix: margin + col * cellW, iy: margin + row * cellH };
}

function posToSlot(ix, iy, cols) {
  const { margin, cellW, cellH } = DESKTOP_GRID;
  const col = Math.max(0, Math.round((ix - margin) / cellW));
  const row = Math.max(0, Math.round((iy - margin) / cellH));
  return row * cols + col;
}

function nextFreeSlot(occupiedSlots, startSlot) {
  for (let s = Math.max(0, startSlot); s < 5000; s++) {
    if (!occupiedSlots.has(s)) return s;
  }
  return Math.max(0, startSlot);
}

function computeOccupiedSlots(visibleMemos, cols, excludeId) {
  const occupied = new Set();
  for (const m of visibleMemos) {
    if (excludeId && m.id === excludeId) continue;
    const slot = posToSlot(m.ix ?? DESKTOP_GRID.margin, m.iy ?? DESKTOP_GRID.margin, cols);
    occupied.add(slot);
  }
  return occupied;
}

function ensureSystemSlots(prevMemos, desktopEl) {
  const layout = getDesktopLayout(desktopEl);
  const visible = prevMemos.filter((m) => !m.isTrashed);
  const about = visible.find((m) => m.id === ABOUT_ID) ?? null;
  const snake = visible.find((m) => m.id === SNAKE_ID) ?? null;
  if (!about && !snake) return prevMemos;

  const systemDesired = [];
  if (about) systemDesired.push({ id: ABOUT_ID, slot: 0 });
  if (snake) systemDesired.push({ id: SNAKE_ID, slot: 1 });
  const systemIds = new Set(systemDesired.map((d) => d.id));

  const occupied = computeOccupiedSlots(visible, layout.cols);

  // Find any non-system occupants sitting in reserved slots.
  const displaced = [];
  for (const d of systemDesired) {
    const p = slotToPos(d.slot, layout.cols);
    const occ = visible.find(
      (m) =>
        !systemIds.has(m.id) &&
        posToSlot(m.ix ?? p.ix, m.iy ?? p.iy, layout.cols) === d.slot
    );
    if (occ) displaced.push(occ.id);
  }

  // Reserve system slots.
  systemDesired.forEach((d) => occupied.delete(d.slot));

  // Re-slot displaced items starting at slot 2.
  const displacedSlots = new Map();
  let start = 2;
  displaced.forEach((id) => {
    const slot = nextFreeSlot(occupied, start);
    occupied.add(slot);
    displacedSlots.set(id, slot);
    start = slot + 1;
  });

  return prevMemos.map((m) => {
    if (m.id === ABOUT_ID) {
      const p = slotToPos(0, layout.cols);
      return { ...m, ix: p.ix, iy: p.iy };
    }
    if (m.id === SNAKE_ID) {
      const p = slotToPos(1, layout.cols);
      return { ...m, ix: p.ix, iy: p.iy };
    }
    const ds = displacedSlots.get(m.id);
    if (ds != null) {
      const p = slotToPos(ds, layout.cols);
      return { ...m, ix: p.ix, iy: p.iy };
    }
    return m;
  });
}

function safeParseMemos(raw) {
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((m) => m && typeof m === "object")
      .map((m) => ({
        id: typeof m.id === "string" ? m.id : `${Date.now()}-${Math.random()}`,
        name: typeof m.name === "string" ? m.name : "untitled",
        content: typeof m.content === "string" ? m.content : "",
        createdAt: Number.isFinite(m.createdAt) ? m.createdAt : 0,
        isTrashed: Boolean(m.isTrashed),
        locked: m?.id === ABOUT_ID || m?.id === SNAKE_ID ? true : Boolean(m.locked),
        ix: Number.isFinite(m.ix) ? m.ix : 14,
        iy: Number.isFinite(m.iy) ? m.iy : 14,
        x: Number.isFinite(m.x) ? m.x : 120,
        y: Number.isFinite(m.y) ? m.y : 72,
        w: Number.isFinite(m.w) ? m.w : 520,
        h: Number.isFinite(m.h) ? m.h : 340,
      }));
  } catch {
    return [];
  }
}

function getNextUntitledName(existingNames) {
  const base = "untitled";
  if (!existingNames.has(base)) return base;
  for (let i = 2; i < 1000; i++) {
    const candidate = `${base} ${i}`;
    if (!existingNames.has(candidate)) return candidate;
  }
  return `${base} ${Date.now()}`;
}

function Application() {
  const [memos, setMemos] = useState(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? safeParseMemos(raw) : [];
  });
  const [openMemoId, setOpenMemoId] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [trashOpen, setTrashOpen] = useState(false);
  const [aboutRejecting, setAboutRejecting] = useState(false);
  const [snapToGrid, setSnapToGrid] = useState(() => {
    try {
      return localStorage.getItem(SNAP_KEY) === "1";
    } catch {
      return false;
    }
  });
  const desktopRef = useRef(null);
  const trashRef = useRef(null);
  const dragRef = useRef(null);
  const aboutRejectTimerRef = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(memos));
    } catch {
      // ignore storage quota / privacy mode errors
    }
  }, [memos]);

  useEffect(() => {
    try {
      localStorage.setItem(SNAP_KEY, snapToGrid ? "1" : "0");
    } catch {
      // ignore
    }
  }, [snapToGrid]);

  // Ensure the built-in About Me file always exists.
  useEffect(() => {
    setMemos((prev) => {
      if (prev.some((m) => m.id === ABOUT_ID)) return prev;
      return [
        {
          id: ABOUT_ID,
          name: ABOUT_NAME,
          content: ABOUT_CONTENT,
          createdAt: 0,
          isTrashed: false,
          locked: true,
          ix: 14,
          iy: 14,
          x: 160,
          y: 90,
          w: 560,
          h: 380,
        },
        ...prev,
      ];
    });
  }, []);

  // Ensure the built-in SNAKE program always exists.
  useEffect(() => {
    setMemos((prev) => {
      if (prev.some((m) => m.id === SNAKE_ID)) return prev;
      return [
        {
          id: SNAKE_ID,
          name: "SNAKE",
          content: "",
          createdAt: 0,
          isTrashed: false,
          locked: true,
          ix: DESKTOP_GRID.margin + DESKTOP_GRID.cellW,
          iy: DESKTOP_GRID.margin,
          x: 220,
          y: 120,
          w: 560,
          h: 420,
        },
        ...prev,
      ];
    });
  }, []);

  // On mount, normalize About/Snake into slots 0 and 1.
  useEffect(() => {
    setMemos((prev) => ensureSystemSlots(prev, desktopRef.current));
  }, []);

  const openMemo = useCallback((id) => setOpenMemoId(id), []);
  const closeMemo = useCallback(() => setOpenMemoId(null), []);

  const activeMemo = useMemo(
    () => memos.find((m) => m.id === openMemoId && !m.isTrashed) ?? null,
    [memos, openMemoId]
  );

  const visibleMemos = useMemo(() => memos.filter((m) => !m.isTrashed), [memos]);
  const trashedMemos = useMemo(() => memos.filter((m) => m.isTrashed), [memos]);
  const deletableMemos = useMemo(
    () => visibleMemos.filter((m) => !m.locked),
    [visibleMemos]
  );

  const createMemo = useCallback(() => {
    const id =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    setMemos((prev) => {
      const visible = prev.filter((m) => !m.isTrashed);
      const existingNames = new Set(prev.map((m) => m.name));
      const name = getNextUntitledName(existingNames);

      const layout = getDesktopLayout(desktopRef.current);
      const occupied = computeOccupiedSlots(visible, layout.cols);

      // Anchor new memo to the most recently created visible icon.
      const anchor =
        visible.reduce((best, m) => (m.createdAt > (best?.createdAt ?? -1) ? m : best), null) ??
        visible.find((m) => m.id === ABOUT_ID) ??
        null;

      const anchorSlot = anchor
        ? posToSlot(anchor.ix ?? DESKTOP_GRID.margin, anchor.iy ?? DESKTOP_GRID.margin, layout.cols)
        : 0;

      const slot = nextFreeSlot(occupied, anchorSlot + 1);
      const { ix, iy } = slotToPos(slot, layout.cols);
      const offset = visible.length * 12;

      return [
        {
          id,
          name,
          content: "",
          createdAt: Date.now(),
          isTrashed: false,
          ix,
          iy,
          x: 120 + offset,
          y: 72 + offset,
          w: 520,
          h: 340,
        },
        ...prev,
      ];
    });

    setOpenMemoId(id);
  }, []);

  const updateActiveMemoContent = useCallback(
    (content) => {
      setMemos((prev) =>
        prev.map((m) => (m.id === openMemoId ? { ...m, content } : m))
      );
    },
    [openMemoId]
  );

  const renameActiveMemo = useCallback(
    (name) => {
      setMemos((prev) =>
        prev.map((m) => (m.id === openMemoId ? { ...m, name } : m))
      );
    },
    [openMemoId]
  );

  const updateActiveMemoFrame = useCallback(
    (frame) => {
      setMemos((prev) =>
        prev.map((m) => (m.id === openMemoId ? { ...m, ...frame } : m))
      );
    },
    [openMemoId]
  );

  const requestDeleteActiveMemo = useCallback(() => {
    if (!openMemoId) return;
    const memo = memos.find((m) => m.id === openMemoId && !m.isTrashed);
    if (!memo) return;
    if (memo.locked) return;
    setConfirm({
      type: "trash-one",
      memoId: openMemoId,
      memoName: memo?.name ?? "this memo",
    });
  }, [memos, openMemoId]);

  const requestDeleteAllMemos = useCallback(() => {
    if (deletableMemos.length === 0) return;
    setConfirm({ type: "trash-all" });
  }, [deletableMemos.length]);

  const requestEmptyTrash = useCallback(() => {
    if (trashedMemos.length === 0) return;
    setConfirm({ type: "empty-trash" });
  }, [trashedMemos.length]);

  const performConfirm = useCallback(() => {
    if (!confirm) return;
    if (confirm.type === "trash-one") {
      setMemos((prev) =>
        prev.map((m) => (m.id === confirm.memoId ? { ...m, isTrashed: true } : m))
      );
      setOpenMemoId((prev) => (prev === confirm.memoId ? null : prev));
    }
    if (confirm.type === "trash-all") {
      setMemos((prev) =>
        prev.map((m) => (m.locked ? m : { ...m, isTrashed: true }))
      );
      setOpenMemoId(null);
    }
    if (confirm.type === "empty-trash") {
      setMemos((prev) => prev.filter((m) => !m.isTrashed));
    }
    setConfirm(null);
  }, [confirm]);

  const restoreFromTrash = useCallback((memoId) => {
    setMemos((prev) => {
      const visibleCount = prev.filter((m) => !m.isTrashed && m.id !== memoId).length;
      const offset = visibleCount * 18;

      // Place restored icon somewhere safe (away from Trash in bottom-right).
      const margin = 14;
      const trashSafePadX = 140;
      const trashSafePadY = 160;
      const maxX = Math.max(margin, window.innerWidth - trashSafePadX);
      const maxY = Math.max(margin, window.innerHeight - trashSafePadY);

      const ix = Math.min(maxX, margin + offset);
      const iy = Math.min(maxY, margin + offset);

      return prev.map((m) =>
        m.id === memoId ? { ...m, isTrashed: false, ix, iy } : m
      );
    });
  }, []);

  const onMemoPointerDown = useCallback((memoId, e) => {
    if (e.button != null && e.button !== 0) return;
    e.preventDefault();
    const memo = memos.find((m) => m.id === memoId);
    if (!memo || memo.isTrashed) return;

    const startX = e.clientX;
    const startY = e.clientY;
    dragRef.current = {
      memoId,
      startX,
      startY,
      startIx: memo.ix ?? 14,
      startIy: memo.iy ?? 14,
      moved: false,
      pointerId: e.pointerId,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, [memos]);

  const onMemoPointerMove = useCallback((e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    if (Math.abs(dx) + Math.abs(dy) > 4) dragRef.current.moved = true;

    const nextIx = dragRef.current.startIx + dx;
    const nextIy = dragRef.current.startIy + dy;

    setMemos((prev) => {
      const memoId = dragRef.current?.memoId;
      if (!memoId) return prev;

      if (!snapToGrid) {
        return prev.map((m) =>
          m.id === memoId ? { ...m, ix: Math.max(0, nextIx), iy: Math.max(0, nextIy) } : m
        );
      }

      const visible = prev.filter((m) => !m.isTrashed);
      const layout = getDesktopLayout(desktopRef.current);
      const occupied = computeOccupiedSlots(visible, layout.cols, memoId);

      const desiredSlot = posToSlot(nextIx, nextIy, layout.cols);
      const slot = occupied.has(desiredSlot)
        ? nextFreeSlot(occupied, desiredSlot)
        : desiredSlot;

      const p = slotToPos(slot, layout.cols);

      const { margin, trashSafePadX, trashSafePadY } = DESKTOP_GRID;
      const maxX = Math.max(margin, layout.width - trashSafePadX);
      const maxY = Math.max(margin, layout.height - trashSafePadY);

      const ix = Math.min(maxX, Math.max(margin, p.ix));
      const iy = Math.min(maxY, Math.max(margin, p.iy));

      return prev.map((m) => (m.id === memoId ? { ...m, ix, iy } : m));
    });
  }, [snapToGrid]);

  const snapAllIconsToGrid = useCallback(() => {
    setMemos((prev) => {
      const visible = prev.filter((m) => !m.isTrashed);
      const layout = getDesktopLayout(desktopRef.current);

      // Reserve slot 0 for About Me and slot 1 for SNAKE (when present).
      const about = visible.find((m) => m.id === ABOUT_ID) ?? null;
      const snake = visible.find((m) => m.id === SNAKE_ID) ?? null;
      const others = visible.filter((m) => m.id !== ABOUT_ID && m.id !== SNAKE_ID);

      const ordered = about
        ? snake
          ? [about, snake, ...others]
          : [about, ...others]
        : snake
          ? [snake, ...others]
          : [...others];

      const occupied = new Set();
      const positionsById = new Map();

      ordered.forEach((m, idx) => {
        // About is always slot 0; Snake is always slot 1 (if About exists); others keep nearest slot.
        let desired =
          m.id === ABOUT_ID
            ? 0
            : m.id === SNAKE_ID
              ? about
                ? 1
                : 0
              : posToSlot(m.ix ?? 14, m.iy ?? 14, layout.cols);

        if (m.id !== ABOUT_ID && m.id !== SNAKE_ID && about && snake) desired = Math.max(2, desired);
        if (m.id !== ABOUT_ID && m.id !== SNAKE_ID && about && !snake) desired = Math.max(1, desired);
        if (m.id !== ABOUT_ID && m.id !== SNAKE_ID && !about && snake) desired = Math.max(1, desired);

        const slot = occupied.has(desired) ? nextFreeSlot(occupied, desired) : desired;
        occupied.add(slot);
        positionsById.set(m.id, slotToPos(slot, layout.cols));
      });

      const { margin, trashSafePadX, trashSafePadY } = DESKTOP_GRID;
      const maxX = Math.max(margin, layout.width - trashSafePadX);
      const maxY = Math.max(margin, layout.height - trashSafePadY);

      return prev.map((m) => {
        if (m.isTrashed) return m;
        const p = positionsById.get(m.id);
        if (!p) return m;
        return { ...m, ix: Math.min(maxX, p.ix), iy: Math.min(maxY, p.iy) };
      });
    });
  }, []);

  const toggleSnapToGrid = useCallback(() => {
    setSnapToGrid((v) => {
      const next = !v;
      if (next) {
        // When enabling, immediately align everything to grid.
        queueMicrotask(() => snapAllIconsToGrid());
      }
      return next;
    });
  }, [snapAllIconsToGrid]);

  const onMemoPointerUp = useCallback((e) => {
    if (!dragRef.current) return;
    const { memoId, moved, pointerId } = dragRef.current;
    dragRef.current = null;
    e.currentTarget.releasePointerCapture?.(pointerId);

    if (!moved) {
      openMemo(memoId);
      return;
    }

    const trashEl = trashRef.current;
    if (!trashEl) return;
    const rect = trashEl.getBoundingClientRect();
    const inside =
      e.clientX >= rect.left &&
      e.clientX <= rect.right &&
      e.clientY >= rect.top &&
      e.clientY <= rect.bottom;

    if (inside) {
      const memo = memos.find((m) => m.id === memoId);
      if (memo?.locked && memoId === ABOUT_ID) {
        // Do the "nope" animation, then snap About Me back to slot0.
        setAboutRejecting(true);
        if (aboutRejectTimerRef.current) {
          clearTimeout(aboutRejectTimerRef.current);
        }
        aboutRejectTimerRef.current = setTimeout(() => {
          setMemos((prev) => ensureSystemSlots(prev, desktopRef.current));
          setAboutRejecting(false);
          aboutRejectTimerRef.current = null;
        }, 950);
        return;
      }
      if (memo?.locked && memoId === SNAKE_ID) {
        setMemos((prev) => ensureSystemSlots(prev, desktopRef.current));
        return;
      }

      setMemos((prev) =>
        prev.map((m) => (m.id === memoId && !m.locked ? { ...m, isTrashed: true } : m))
      );
      setOpenMemoId((prev) => (prev === memoId ? null : prev));
    }
  }, [openMemo, memos]);

  const arrangeIcons = useCallback(() => {
    const el = desktopRef.current;
    const rect = el?.getBoundingClientRect?.();
    const width = rect?.width ?? window.innerWidth;
    const height = rect?.height ?? Math.max(0, window.innerHeight - 28);

    const margin = 14;
    const cellW = 104;
    const cellH = 100;

    // Keep a safe area for the trash can in bottom-right.
    const trashSafePadX = 140;
    const trashSafePadY = 170;
    const usableW = Math.max(cellW, width - trashSafePadX);
    const usableH = Math.max(cellH, height - margin);

    const cols = Math.max(1, Math.floor((usableW - margin * 2) / cellW));

    setMemos((prev) => {
      const visible = prev.filter((m) => !m.isTrashed);
      const about = visible.find((m) => m.id === ABOUT_ID) ?? null;
      const snake = visible.find((m) => m.id === SNAKE_ID) ?? null;
      const rest = visible.filter((m) => m.id !== ABOUT_ID && m.id !== SNAKE_ID);

      // Build a deterministic order: About first, then keep current array order for the rest.
      const ordered = about
        ? snake
          ? [about, snake, ...rest]
          : [about, ...rest]
        : snake
          ? [snake, ...rest]
          : [...rest];

      // Precompute positions by slot so About always wins slot 0.
      const maxX = Math.max(margin, width - trashSafePadX);
      const maxY = Math.max(margin, usableH - trashSafePadY);

      const positionsById = new Map();
      ordered.forEach((m, idx) => {
        const slot = idx; // About is idx 0 when present
        const col = slot % cols;
        const row = Math.floor(slot / cols);
        const ix = Math.min(maxX, margin + col * cellW);
        const iy = Math.min(maxY, margin + row * cellH);
        positionsById.set(m.id, { ix, iy });
      });

      return prev.map((m) => {
        if (m.isTrashed) return m;
        const p = positionsById.get(m.id);
        return p ? { ...m, ix: p.ix, iy: p.iy } : m;
      });
    });
  }, []);

  return (
    <div className="mac-root">
      <MenuBar
        onNewMemo={createMemo}
        onDeleteMemo={requestDeleteActiveMemo}
        onDeleteAllMemos={requestDeleteAllMemos}
        onArrangeIcons={arrangeIcons}
        snapToGrid={snapToGrid}
        onToggleSnapToGrid={toggleSnapToGrid}
        hasOpenMemo={Boolean(activeMemo)}
        canDeleteMemo={Boolean(activeMemo && !activeMemo.locked)}
        hasDeletableMemos={deletableMemos.length > 0}
      />
      <main
        ref={desktopRef}
        className="mac-desktop"
        role="main"
        aria-label="Desktop"
      >
        <div className="mac-desktopIcons" aria-label="Desktop icons">
          {visibleMemos.map((m) => (
            <DesktopIcon
              key={m.id}
              title={m.name}
              style={{ left: m.ix ?? 14, top: m.iy ?? 14, position: "absolute" }}
              glyphVariant={m.id === ABOUT_ID ? "about" : m.id === SNAKE_ID ? "snake" : "default"}
              className={
                m.id === ABOUT_ID
                  ? `mac-icon--about ${aboutRejecting ? "mac-icon--reject" : ""}`.trim()
                  : m.id === SNAKE_ID
                    ? "mac-icon--snake"
                  : ""
              }
              onOpen={() => {}}
              onPointerDown={(e) => onMemoPointerDown(m.id, e)}
              onPointerMove={onMemoPointerMove}
              onPointerUp={onMemoPointerUp}
            />
          ))}
        </div>

        <TrashIcon
          ref={trashRef}
          count={trashedMemos.length}
          onOpen={() => setTrashOpen(true)}
        />

        {activeMemo ? (
          <div className="mac-windowLayer" aria-label="Windows">
            <MemoWindow
              title={activeMemo.name}
              value={activeMemo.content}
              x={activeMemo.x ?? 120}
              y={activeMemo.y ?? 72}
              width={activeMemo.w ?? 520}
              height={activeMemo.h ?? 340}
              readOnly={Boolean(activeMemo.locked)}
              canRename={!activeMemo.locked}
              variant={activeMemo.id === ABOUT_ID ? "about" : activeMemo.id === SNAKE_ID ? "snake" : "memo"}
              onChange={updateActiveMemoContent}
              onRename={renameActiveMemo}
              onFrameChange={updateActiveMemoFrame}
              onClose={closeMemo}
            />
          </div>
        ) : null}

        {trashOpen ? (
          <div className="mac-windowLayer" aria-label="Windows">
            <TrashWindow
              items={trashedMemos}
              onRestore={restoreFromTrash}
              onEmptyTrash={requestEmptyTrash}
              onClose={() => setTrashOpen(false)}
            />
          </div>
        ) : null}

        {confirm ? (
          <div className="mac-windowLayer" aria-label="Dialogs">
            <ConfirmDialog
              title="Delete"
              message={
                confirm.type === "trash-one"
                  ? `Move “${confirm.memoName}” to Trash?`
                  : confirm.type === "trash-all"
                    ? "Move all memos to Trash?"
                    : "Empty Trash? This can’t be undone."
              }
              confirmText={confirm.type === "empty-trash" ? "Empty" : "Move"}
              cancelText="Cancel"
              onConfirm={performConfirm}
              onCancel={() => setConfirm(null)}
            />
          </div>
        ) : null}
      </main>
    </div>
  );
}
export default Application;
